import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Container,
  Header,
  Table,
  Button,
  Icon,
  Message,
  Segment,
  Dimmer,
  Loader,
  Confirm,
  Input,
  Divider,
  Label,
  Form,
  Dropdown,
  Grid,
  Card,
  Statistic
} from 'semantic-ui-react';
import {
  fetchSecurityDefinitions,
  refreshSecurityRegistry,
  deleteSecurityDefinition,
  fetchRegistryStatus,
  selectSecurityDefinitions,
  selectIsLoading,
  selectIsRefreshing,
  selectError,
  selectRegistryStatus,
  selectLastRefresh,
  selectSseConnectionState,
  selectSseError,
  selectIsSseConnected,
  setSseConnectionState,
  setSseError,
  handleSseRegistryEvent,
  clearError,
  clearSseError
} from '../../slices/securityRegistrySlice';
import sseService from '../../services/sseService';
import { config } from '../../constants/config';
import '../../styles/securityRegistry.css'
import { AuthStdFooter } from "../../utils/auth-std-footer";

/**
 * SecurityDefinitionsList component for displaying and managing security definitions
 * Mirrors PathDefinitionsList structure for consistency
 * @returns {JSX.Element} SecurityDefinitionsList component
 */
const SecurityDefinitionsList = () => {
  const dispatch = useDispatch();

  // Get security definitions data from Redux
  const definitions = useSelector(selectSecurityDefinitions);
  const isLoading = useSelector(selectIsLoading);
  const isRefreshing = useSelector(selectIsRefreshing);
  const error = useSelector(selectError);
  const registryStatus = useSelector(selectRegistryStatus);
  const lastRefresh = useSelector(selectLastRefresh);

  // SSE connection state
  const sseConnectionState = useSelector(selectSseConnectionState);
  const sseError = useSelector(selectSseError);
  const isSseConnected = useSelector(selectIsSseConnected);

  // Local state
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [localError, setLocalError] = useState(null);
  const [localSuccess, setLocalSuccess] = useState(null);
  const [filterCriticality, setFilterCriticality] = useState('');
  const [filterEntityType, setFilterEntityType] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Fetch security definitions on component mount
  useEffect(() => {
    dispatch(fetchSecurityDefinitions());
    dispatch(fetchRegistryStatus());
  }, [dispatch]);

  // Setup SSE connection for real-time updates
  useEffect(() => {
    const sseEndpoint = `${config.BASE_URL}/security-registry/events`;

    // Setup SSE event handlers
    const handleRegistryChange = (data) => {
      console.log('SSE: Registry change received:', data);
      dispatch(handleSseRegistryEvent(data));
    };

    const handleConnectionStateChange = (state) => {
      console.log('SSE: Connection state changed:', state);
      dispatch(setSseConnectionState(state));
    };

    const handleSseError = (error) => {
      console.error('SSE: Connection error:', error);
      dispatch(setSseError(error));
    };

    // Add event listeners
    sseService.addEventListener('registryChanged', handleRegistryChange);
    sseService.addStateListener(handleConnectionStateChange);

    // Connect to SSE endpoint
    sseService.connect(sseEndpoint);

    // Cleanup on unmount
    return () => {
      sseService.removeEventListener('registryChanged', handleRegistryChange);
      sseService.removeStateListener(handleConnectionStateChange);
      sseService.disconnect();
    };
  }, [dispatch]);

  // Update local state when Redux state changes
  useEffect(() => {
    if (error) {
      setLocalError(error);
      setTimeout(() => {
        dispatch(clearError());
        setLocalError(null);
      }, 5000);
    }
  }, [dispatch, error]);

  // Handle SSE errors
  useEffect(() => {
    if (sseError) {
      setLocalError(`Real-time connection error: ${sseError}`);
      setTimeout(() => {
        dispatch(clearSseError());
      }, 5000);
    }
  }, [dispatch, sseError]);

  // Handle delete confirmation
  const handleDeleteClick = (name) => {
    setDeleteTarget(name);
    setConfirmOpen(true);
  };

  // Handle delete confirmation cancel
  const handleCancel = () => {
    setConfirmOpen(false);
    setDeleteTarget(null);
  };

  // Handle delete confirmation confirm
  const handleConfirm = async () => {
    if (deleteTarget) {
      try {
        await dispatch(deleteSecurityDefinition(deleteTarget)).unwrap();
        setLocalSuccess(`Security definition "${deleteTarget}" deleted successfully`);
      } catch (err) {
        setLocalError(`Failed to delete security definition: ${err.message || 'Unknown error'}`);
      }
    }
    setConfirmOpen(false);
    setDeleteTarget(null);
  };

  // Handle refresh
  const handleRefresh = async () => {
    try {
      await dispatch(refreshSecurityRegistry()).unwrap();
      await dispatch(fetchSecurityDefinitions()).unwrap();
      setLocalSuccess('Security registry refreshed successfully');
    } catch (err) {
      setLocalError(`Failed to refresh security registry: ${err.message || 'Unknown error'}`);
    }
  };

  // Helper functions
  const getCriticalityColor = (criticality) => {
    switch (criticality) {
      case 'CRITICAL': return 'red';
      case 'HIGH': return 'orange';
      case 'MEDIUM': return 'yellow';
      case 'LOW': return 'green';
      default: return 'grey';
    }
  };

  const getCriticalityIcon = (criticality) => {
    switch (criticality) {
      case 'CRITICAL': return 'exclamation triangle';
      case 'HIGH': return 'warning sign';
      case 'MEDIUM': return 'info circle';
      case 'LOW': return 'check circle';
      default: return 'question circle';
    }
  };

  const getEntityTypeIcon = (entityType) => {
    switch (entityType) {
      case 'Opportunity': return 'dollar sign';
      case 'Application': return 'file alternate';
      case 'Message': return 'comment';
      case 'BookProfile': return 'book';
      default: return 'file';
    }
  };

  // Filter definitions based on search term and filters
  const filteredDefinitions = definitions.filter(def => {
    if (searchTerm && !def.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !def.description.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (filterCriticality && def.criticality !== filterCriticality) return false;
    if (filterEntityType && def.entityType !== filterEntityType) return false;
    if (!showInactive && !def.active) return false;
    return true;
  });

  // Get unique values for filters
  const criticalityOptions = [...new Set(definitions.map(def => def.criticality))]
    .map(criticality => ({ key: criticality, text: criticality, value: criticality }));

  const entityTypeOptions = [...new Set(definitions.map(def => def.entityType))]
    .map(entityType => ({ key: entityType, text: entityType, value: entityType }));

  return (
    <div className="page-container" style={{ marginBottom: "70px" }}>
      <Container>
        <Segment>
          <Header as="h2">
            <Icon name="shield alternate" />
            <Header.Content>
              Security Registry Management
              <Header.Subheader>
                Manage security definitions for transactional entities
                {lastRefresh && (
                  <span style={{ marginLeft: '10px', fontSize: '0.9em', color: '#666' }}>
                    Last refreshed: {new Date(lastRefresh).toLocaleString()}
                  </span>
                )}
                {/* SSE Connection Status */}
                <div style={{ marginTop: '5px' }}>
                  {sseConnectionState === 'connected' && (
                    <Label size="mini" color="green">
                      <Icon name="wifi" />
                      Real-time updates active
                    </Label>
                  )}
                  {sseConnectionState === 'connecting' && (
                    <Label size="mini" color="yellow">
                      <Icon name="circle notched" loading />
                      Connecting...
                    </Label>
                  )}
                  {sseConnectionState === 'error' && (
                    <Label size="mini" color="red">
                      <Icon name="warning sign" />
                      Connection error - using manual refresh
                    </Label>
                  )}
                  {sseConnectionState === 'disconnected' && (
                    <Label size="mini" color="grey">
                      <Icon name="wifi" />
                      Real-time updates disabled
                    </Label>
                  )}
                </div>
              </Header.Subheader>
            </Header.Content>
          </Header>
          <Divider />

          {/* Registry Status Cards */}

          {registryStatus && (
            <div className='sr-statistic-container'>
              <div className='sr-statistic'>
                <span className='sr-statistic'>({registryStatus.definitionCount})  </span>
                <span className='sr-statistic-name' >Definitions</span>
              </div>
              <div className='sr-statistic' >
                <span className='sr-statistic'>({definitions.filter(def => def.criticality === 'CRITICAL').length}) </span>
                <span className='sr-statistic-name' >Critical</span>
              </div>
              <div className='sr-statistic' >
                <span className='sr-statistic'>({definitions.filter(def => def.active).length}) </span>
                <span className='sr-statistic-name' >Active</span>
              </div>
              <div className='sr-statistic' >
                <span className='sr-statistic'>({registryStatus.auditTrailSize}) </span>
                <span className='sr-statistic-name' >Audit Entries</span>
              </div>

            </div>
          )}

          {/* Action buttons */}
          <div style={{ marginBottom: '1em', display: 'flex', justifyContent: 'space-between' }}>
            <Button
              as={Link}
              to="/admin/security-registry/create"
              primary
              icon
              labelPosition="left"
            >
              <Icon name="plus" />
              Create New Security Definition
            </Button>
            <Button
              icon
              labelPosition="left"
              color="blue"
              loading={isRefreshing}
              onClick={handleRefresh}
            >
              <Icon name="refresh" />
              Refresh Registry
            </Button>
          </div>

          {/* Search and filters */}
          <Input
            icon="search"
            placeholder="Search security definitions..."
            fluid
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ marginBottom: '1em' }}
          />

          <div style={{ marginBottom: '1em', display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 'bold', fontSize: '16px', color: '#333' }}>Filters:</span>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontWeight: 'bold', minWidth: 'fit-content' }}>Criticality:</span>
              <Dropdown
                placeholder="All"
                clearable
                selection
                options={criticalityOptions}
                value={filterCriticality}
                onChange={(e, { value }) => setFilterCriticality(value)}
                style={{ minWidth: '120px' }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontWeight: 'bold', minWidth: 'fit-content' }}>Entity Type:</span>
              <Dropdown
                placeholder="All"
                clearable
                selection
                options={entityTypeOptions}
                value={filterEntityType}
                onChange={(e, { value }) => setFilterEntityType(value)}
                style={{ minWidth: '120px' }}
              />
            </div>

            <div>
              <Button
                toggle
                active={showInactive}
                onClick={() => setShowInactive(!showInactive)}
                content="Show Inactive"
              />
            </div>

            {/* Clear Filters Button - only show if any filters are active */}
            {(filterCriticality || filterEntityType || showInactive) && (
              <div>
                <Button
                  icon
                  labelPosition="left"
                  onClick={() => {
                    setFilterCriticality('');
                    setFilterEntityType('');
                    setShowInactive(false);
                  }}
                  color="grey"
                  size="small"
                >
                  <Icon name="remove" />
                  Clear Filters
                </Button>
              </div>
            )}
            {(filterCriticality || filterEntityType || showInactive) && (
              <div style={{ marginLeft: 'auto' }}>
                <Label size="small" color="blue">
                  <Icon name="filter" />
                  {[filterCriticality, filterEntityType, showInactive ? 'Inactive' : null]
                    .filter(Boolean).length} filter(s) active
                </Label>
              </div>
            )}
          </div>



          {/* Messages */}
          {localSuccess && (
            <Message positive>
              <Message.Header>Success</Message.Header>
              <p>{localSuccess}</p>
            </Message>
          )}

          {localError && (
            <Message negative>
              <Message.Header>Error</Message.Header>
              <p>{localError}</p>
            </Message>
          )}

          {/* Security definitions table */}
          {isLoading ? (
            <Segment>
              <Dimmer active inverted>
                <Loader inverted>Loading Security Definitions</Loader>
              </Dimmer>
              <div style={{ height: '200px' }}></div>
            </Segment>
          ) : definitions.length === 0 ? (
            <Message info>
              <Message.Header>No Security Definitions Found</Message.Header>
              <p>
                No security definitions have been created yet. Click the "Create New Security Definition" button to create one.
              </p>
            </Message>
          ) : filteredDefinitions.length === 0 ? (
            <Message warning>
              <Message.Header>No Matching Security Definitions</Message.Header>
              <p>
                No security definitions match your search criteria. Try adjusting your search term or filters.
              </p>
            </Message>
          ) : (
            <Table celled striped>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Name</Table.HeaderCell>
                  <Table.HeaderCell>Entity Type</Table.HeaderCell>
                  <Table.HeaderCell>Criticality</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Version</Table.HeaderCell>
                  {/* <Table.HeaderCell>Last Updated</Table.HeaderCell> */}
                  <Table.HeaderCell>Actions</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filteredDefinitions.map((def) => (
                  <Table.Row key={def.name}>
                    <Table.Cell>
                      <strong>{def.name}</strong>
                      {def.description && <div><small>{def.description}</small></div>}
                    </Table.Cell>
                    <Table.Cell style={{ width: '130px' }}>
                      <Label>
                        <Icon name={getEntityTypeIcon(def.entityType)} />
                        {def.entityType}
                      </Label>
                    </Table.Cell>
                    <Table.Cell style={{ width: '120px' }}>
                      <Label color={getCriticalityColor(def.criticality)}>
                        <Icon name={getCriticalityIcon(def.criticality)} />
                        {def.criticality}
                      </Label>
                    </Table.Cell>
                    <Table.Cell style={{ width: '90px' }}>
                      {def.active ? (
                        <span style={{ color: 'green' }}>
                          <Icon name="check circle" /> Active
                        </span>
                      ) : (
                        <span style={{ color: 'red' }}>
                          <Icon name="times circle" /> Inactive
                        </span>
                      )}
                    </Table.Cell>
                    <Table.Cell>{def.version || '1.0.0'}</Table.Cell>
                    {/* <Table.Cell>
                      {def.updatedAt
                        ? new Date(def.updatedAt).toLocaleDateString()
                        : 'N/A'}
                    </Table.Cell> */}
                    <Table.Cell>
                      <Button.Group size="small">
                        <Button
                          as={Link}
                          to={`/admin/security-registry/${def.name}/view`}
                          icon
                          labelPosition="left"
                          primary
                          size="small"
                        >
                          <Icon name="eye" />
                          View
                        </Button>
                        <Button
                          as={Link}
                          to={`/admin/security-registry/${def.name}/edit`}
                          icon
                          labelPosition="left"
                          color="yellow"
                          size="small"
                        >
                          <Icon name="edit" />
                          Edit
                        </Button>
                        <Button
                          icon
                          labelPosition="left"
                          color="red"
                          size="small"
                          onClick={() => handleDeleteClick(def.name)}
                        >
                          <Icon name="trash" />
                          Delete
                        </Button>
                      </Button.Group>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}

          {/* Delete confirmation dialog */}
          <Confirm
            open={confirmOpen}
            header="Delete Security Definition"
            content={`Are you sure you want to delete the security definition "${deleteTarget}"? This action cannot be undone and may affect system security.`}
            confirmButton="Delete"
            onCancel={handleCancel}
            onConfirm={handleConfirm}
          />
        </Segment>
      </Container>
      {AuthStdFooter()}
    </div>
  );
};

export default SecurityDefinitionsList;
